import { useEffect, useState } from 'react'
import api from '../services/api'

// Dependentes do funcionário (TMOTCA7 do ERP, frota.motca7). Lançamento é numerado sozinho; excluir não pode.

interface Dependente { lan: number; nom: string; dna: string; doc: string; par: string; pla: string }

const fmtData = (iso: string) => (iso ? iso.split('-').reverse().join('/') : '')
const largura = (max: number) => Math.max(56, max * 9 + 28)
const LINHA: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: '0.75rem 0.9rem', alignItems: 'flex-end' }
const VAZIO: Omit<Dependente, 'lan'> & { lan: number } = { lan: 0, nom: '', dna: '', doc: '', par: '', pla: 'N' }

export default function FuncionarioDependentesModal({ motcod, motnome, onClose }: { motcod: string; motnome: string; onClose: () => void }) {
  const [lista, setLista] = useState<Dependente[] | null>(null)
  const [f, setF] = useState<Dependente>(VAZIO)
  const [erro, setErro] = useState('')
  const [aviso, setAviso] = useState('')
  const [salvando, setSalvando] = useState(false)

  async function carregar(manterLan?: number) {
    const r = await api.get(`/funcionarios/${encodeURIComponent(motcod)}/dependentes`)
    setLista(r.data)
    const alvo = r.data.find((x: Dependente) => x.lan === manterLan)
    setF(alvo ?? (manterLan === undefined && r.data[0]) ?? VAZIO)
  }

  useEffect(() => {
    carregar().catch(() => setErro('Não foi possível carregar os dependentes do funcionário.'))
  }, [motcod])

  async function salvar() {
    setErro(''); setAviso('')
    if (!f.nom.trim()) return setErro('Informe o nome do dependente.')
    setSalvando(true)
    try {
      const r = await api.put(`/funcionarios/${encodeURIComponent(motcod)}/dependentes`, f)
      await carregar(r.data.lan)
      setAviso(r.data?.semAlteracao ? 'Nenhuma alteração para salvar.' : 'Dependente salvo.')
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao salvar o dependente.')
    } finally {
      setSalvando(false)
    }
  }

  const set = (c: keyof Dependente, v: string) => setF(p => ({ ...p, [c]: v }))
  const campo = (w: number, label: string, conteudo: React.ReactNode) => (
    <div className="field-cad" style={{ flex: `0 0 ${w}px` }}>
      <label style={{ whiteSpace: 'nowrap' }}>{label}</label>
      {conteudo}
    </div>
  )
  const txt = (c: keyof Dependente, label: string, max: number) =>
    campo(Math.max(largura(max), label.length * 7 + 10), label,
      <input value={String(f[c] ?? '')} maxLength={max} style={{ width: '100%', boxSizing: 'border-box' }}
        onChange={e => set(c, e.target.value.toUpperCase())} />)

  const th: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.72rem' }
  const td: React.CSSProperties = { padding: '0.35rem 0.6rem', fontSize: '0.82rem' }

  return (
    <div className="modal-overlay" style={{ zIndex: 1300 }} onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth: 760, maxHeight: '92vh', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()}>

        <div className="modal-header" style={{ gap: '1rem' }}>
          <div style={{ minWidth: 0 }}>
            <h2>Dependentes — {motcod} · {motnome}</h2>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexShrink: 0, marginLeft: 'auto' }}>
            <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(219,234,254,0.8)' }}>TMOTCA7</span>
            <button className="modal-fechar" onClick={onClose}>✕</button>
          </div>
        </div>

        <div className="modal-corpo">
          {!lista ? (
            <p style={{ color: erro ? '#dc2626' : '#6b7280' }}>{erro || 'Carregando...'}</p>
          ) : (
            <>
              <div style={{ maxHeight: 220, overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: 8 }}>
                <table className="table" style={{ width: '100%', margin: 0 }}>
                  <thead>
                    <tr>
                      <th style={{ ...th, width: 50 }}>Lanç.</th>
                      <th style={th}>Nome</th>
                      <th style={th}>Dt Nasc.</th>
                      <th style={th}>Documento</th>
                      <th style={th}>Parentesco</th>
                      <th style={th}>Plano</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lista.length === 0 && (
                      <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: '#6b7280', padding: '1rem' }}>Nenhum dependente cadastrado.</td></tr>
                    )}
                    {lista.map(x => {
                      const ativo = x.lan === f.lan
                      return (
                        <tr key={x.lan} onClick={() => { setF(x); setErro(''); setAviso('') }}
                          style={{ cursor: 'pointer', background: ativo ? '#eef3fb' : undefined, fontWeight: ativo ? 600 : undefined }}>
                          <td style={td}>{x.lan}</td>
                          <td style={td}>{x.nom}</td>
                          <td style={td}>{fmtData(x.dna) || '—'}</td>
                          <td style={td}>{x.doc || '—'}</td>
                          <td style={td}>{x.par || '—'}</td>
                          <td style={td}>{x.pla === 'S' ? 'Sim' : 'Não'}</td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', margin: '1rem 0 0.6rem' }}>
                <span style={{ fontSize: '0.72rem', fontWeight: 700, color: '#1a6ea8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  {f.lan ? `Lançamento ${f.lan}` : 'Novo dependente'}
                </span>
                {!!f.lan && (
                  <button className="btn btn-secondary btn-sm" onClick={() => { setF(VAZIO); setErro(''); setAviso('') }}>+ Novo dependente</button>
                )}
              </div>

              <div style={LINHA}>
                {txt('nom', 'Nome *', 30)}
                {campo(140, 'Dt Nascimento',
                  <input type="date" value={f.dna} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('dna', e.target.value)} />)}
              </div>
              <div style={{ ...LINHA, marginTop: '0.75rem' }}>
                {txt('doc', 'Documento', 20)}
                {txt('par', 'Parentesco', 20)}
                {campo(150, 'Possui plano de saúde?',
                  <select value={f.pla === 'S' ? 'S' : 'N'} style={{ width: '100%', boxSizing: 'border-box' }} onChange={e => set('pla', e.target.value)}>
                    <option value="N">Não</option>
                    <option value="S">Sim</option>
                  </select>)}
              </div>
            </>
          )}

          {lista && erro && <p style={{ color: '#dc2626', fontSize: '0.82rem', margin: '1rem 0 0' }}>{erro}</p>}
          {aviso && <p style={{ color: '#16a34a', fontSize: '0.82rem', margin: '1rem 0 0' }}>{aviso}</p>}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Fechar</button>
          <button className="btn btn-orange" onClick={salvar} disabled={salvando || !lista}>{salvando ? 'Salvando...' : 'Salvar'}</button>
        </div>
      </div>
    </div>
  )
}
